// Remote-player rendering, 3D (docs/multiplayer-sync/roadmap.md Phase 4:
// "3D: reuse the existing biped character renderer"). Reuses
// `PlayerAvatar` (FirstPersonController.tsx) exactly as the local player's
// own avatar does - the same shared prototype rig, the same idle/walk/
// sprint locomotion selection - just fed a synthetic `PlayerMotionState`
// derived from the interpolator's own sampling each frame instead of a
// real controller's live physics state.
//
// A fixed, generic avatar for every remote player: whether a creator gets
// any control over how a remote player looks is roadmap section 4's own
// open question, deliberately left unresolved there and not decided here -
// every remote player renders identically to the local player's own
// default appearance.
import { useFrame } from '@react-three/fiber';
import { useRef } from 'react';
import type { Group } from 'three';

import { PlayerAvatar, type PlayerAvatarCustomBody, type PlayerConfig, type PlayerMotionState } from '../FirstPersonController';
import type { Interpolator, InterpolatedTransform } from './interpolator';

/** Below this speed (metres/second), a remote player reads as idle rather
 * than walking - filters out the small jitter inherent to interpolating
 * between two independently-broadcast samples, which would otherwise
 * flicker the walk animation on and off while a remote player is actually
 * standing still. */
const MOVING_SPEED_THRESHOLD = 0.05;

interface RemotePlayerAvatarProps {
  id: string;
  interpolator: Interpolator<InterpolatedTransform>;
  config?: Partial<PlayerConfig>;
  customBody?: PlayerAvatarCustomBody;
}

function RemotePlayerAvatar({ id, interpolator, config, customBody }: RemotePlayerAvatarProps) {
  const groupRef = useRef<Group>(null);
  const motionStateRef = useRef<PlayerMotionState | null>(null);
  // The previous frame's own sample, purely to derive a speed estimate for
  // locomotion clip selection (PlayerAvatar's own selectLocomotionClip) -
  // this engine has no real velocity for a remote player, only positions
  // to compare frame to frame.
  const lastRef = useRef<{ x: number; z: number; time: number } | null>(null);

  useFrame(() => {
    const now = Date.now();
    const sample = interpolator.sampleOne(id, now);
    if (!sample) return; // no transform received for this player yet
    const group = groupRef.current;
    if (group) {
      group.position.set(sample.x, sample.y, sample.z);
      group.rotation.y = sample.yaw;
    }
    const last = lastRef.current;
    const dt = last ? (now - last.time) / 1000 : 0;
    const speed = last && dt > 0 ? Math.hypot(sample.x - last.x, sample.z - last.z) / dt : 0;
    lastRef.current = { x: sample.x, z: sample.z, time: now };
    motionStateRef.current = {
      x: sample.x,
      y: sample.y,
      z: sample.z,
      yaw: sample.yaw,
      pitch: 0,
      // Always fully third-person: `blend` drives PlayerAvatar's
      // first-person head-collapse (so the local camera doesn't render
      // from inside its own skull), which only makes sense for whichever
      // camera actually sits inside this player's own head - never true
      // for anyone but the local player looking through their own eyes.
      blend: 1,
      speed,
      isMoving: speed > MOVING_SPEED_THRESHOLD,
      velocityX: 0,
      velocityZ: 0,
      floor: 0,
    };
  });

  return (
    <group ref={groupRef}>
      <PlayerAvatar motionStateRef={motionStateRef} config={config} customBody={customBody} />
    </group>
  );
}

export interface RemotePlayersProps {
  /** Every currently-connected remote player's id (relay presence, minus
   * the local player) - decides which avatars are mounted at all; each
   * mounted avatar then samples its own per-frame position from
   * `interpolator` independently, so this list only needs to change when
   * presence itself does (a join/leave), not every frame. */
  ids: readonly string[];
  interpolator: Interpolator<InterpolatedTransform>;
  /** Same shape `FirstPersonController`/`PlayerAvatar` take for the local
   * player, so a remote avatar matches the project's own configured pace/
   * height rather than the engine's bare defaults. */
  config?: Partial<PlayerConfig>;
  /** The same project-chosen player body the local `PlayerAvatar` renders
   * (`GameRuntime.tsx`'s `resolvePlayerCharacter`), so a ship with a custom
   * body selected shows every remote player in that body too, not just the
   * local one - every client resolves the same body from the same
   * published roster/`GameSetupDoc`, so no wire-protocol change is needed. */
  customBody?: PlayerAvatarCustomBody;
}

/** One `PlayerAvatar` per connected remote player (roadmap Phase 4),
 * positioned from the shared `Interpolator`'s own per-frame sampling
 * rather than any authored placement - the live, session-only counterpart
 * to `PlacedCharacters`' authored ones. */
export function RemotePlayers({ ids, interpolator, config, customBody }: RemotePlayersProps) {
  if (ids.length === 0) return null;
  return (
    <>
      {ids.map((id) => (
        <RemotePlayerAvatar key={id} id={id} interpolator={interpolator} config={config} customBody={customBody} />
      ))}
    </>
  );
}
